import { useEffect } from "react"

/**
 * Standalone (iOS Home Screen PWA) session recovery.
 *
 * In standalone display mode iOS gives the WebKit process a tighter memory
 * ceiling and no browser chrome to reload from. When iOS discards or
 * long-suspends the backgrounded process, the app resumes into a dead JS
 * context that looks frozen — can't scroll, can't tap, only a hard quit
 * recovers it. Safari tabs hide this by transparently reloading; standalone
 * apps don't, so we reload ourselves.
 *
 * This module touches window/document, so it must only be installed from the
 * client Inertia entrypoint — never from the SSR entrypoint. Importing it is
 * side-effect free (all window access lives inside functions).
 */

// How long the app may sit hidden before a resume forces a fresh reload.
const STALE_AFTER_MS = 30 * 60 * 1000

// Ref-counted registry of "don't reload, there's unsaved work" holders. We use
// a count (not a boolean) so multiple dirty editors compose and one of them
// unmounting can't strand the block for the others.
let blockCount = 0

declare global {
  interface Window {
    // Escape hatch for non-React surfaces to suppress the recovery reload.
    __brainDownBlockAutoReload?: boolean
  }
}

/**
 * Register one hold against the recovery reload. Returns a release function;
 * calling it more than once is a no-op so it can be wired straight into a React
 * cleanup without guarding.
 */
export function blockAutoReload(): () => void {
  blockCount += 1
  let released = false
  return () => {
    if (released) return
    released = true
    blockCount = Math.max(0, blockCount - 1)
  }
}

function isReloadBlocked(): boolean {
  return blockCount > 0 || window.__brainDownBlockAutoReload === true
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Install the standalone recovery guard. No-op outside standalone mode. Call
 * once from the client entrypoint after the DOM is available.
 */
export function installStandaloneRecovery(): void {
  if (typeof window === "undefined" || !isStandalone()) return

  const reload = () => {
    if (isReloadBlocked()) return
    window.location.reload()
  }

  // (a) bfcache restore: pages restored from the back/forward cache resume an
  // old, possibly-stale JS heap. Reload to get a clean context.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) reload()
  })

  // (b) Long suspend: track when we go hidden and, on return, reload if we sat
  // backgrounded long enough that iOS may have torn the process down.
  let hiddenAt: number | null = null
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now()
    } else if (document.visibilityState === "visible") {
      if (hiddenAt !== null && Date.now() - hiddenAt > STALE_AFTER_MS) {
        reload()
      }
      hiddenAt = null
    }
  })
}

/**
 * React hook: hold a recovery-reload block while `active` is true (e.g. from the
 * first unsaved edit until the save lands), releasing it on change/unmount.
 */
export function useBlockAutoReload(active: boolean): void {
  useEffect(() => {
    if (!active) return
    return blockAutoReload()
  }, [active])
}
